import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  Send as SendIcon,
} from "@material-ui/icons";
import { usePdvStyles } from "../styles/pdvStyles";
import clsx from "clsx";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

// Filter out invalid active principle values (data import issue - "Sim"/"Não" in wrong field)
const getValidActivePrinciple = (value) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (["sim", "não", "nao"].includes(trimmed.toLowerCase())) {
    return null;
  }
  return trimmed;
};

const PdvProductItem = ({
  product,
  isSelected = false,
  onAdd,
  onSend,
  onClick,
  showStock = true,
  isSending = false,
}) => {
  const classes = usePdvStyles();
  const theme = useTheme();
  const [quantity, setQuantity] = useState(1);

  // Extract stock quantity - handle both number and object formats
  // API returns stock as object: { quantity, availableQuantity, reservedQuantity, minQuantity }
  const getStockQuantity = () => {
    // Direct stockQuantity field (number)
    if (typeof product.stockQuantity === 'number') {
      return product.stockQuantity;
    }
    // stock as object with quantity property
    if (product.stock && typeof product.stock === 'object') {
      return product.stock.availableQuantity ?? product.stock.quantity ?? 0;
    }
    // stock as number
    if (typeof product.stock === 'number') {
      return product.stock;
    }
    return null; // No stock info
  };

  const stockValue = getStockQuantity();
  const hasStockControl = stockValue !== null;
  const stockQuantity = stockValue ?? 0;
  const isLowStock = hasStockControl && stockQuantity > 0 && stockQuantity <= 10;
  const isOutOfStock = hasStockControl && stockQuantity <= 0;

  const handleIncrease = useCallback((e) => {
    e.stopPropagation();
    setQuantity(prev => {
      // Respect stock limit if stock control is enabled
      if (hasStockControl && prev >= stockQuantity) {
        return prev;
      }
      return prev + 1;
    });
  }, [hasStockControl, stockQuantity]);

  const handleDecrease = useCallback((e) => {
    e.stopPropagation();
    setQuantity(prev => Math.max(1, prev - 1));
  }, []);

  const handleAddToCart = useCallback((e) => {
    e.stopPropagation();
    if (!isOutOfStock && onAdd) {
      onAdd(product, quantity);
      setQuantity(1); // Reset quantity after adding
    }
  }, [isOutOfStock, onAdd, product, quantity]);

  const handleSendProduct = useCallback((e) => {
    e.stopPropagation();
    if (onSend) {
      onSend(product);
    }
  }, [onSend, product]);

  const handleClick = () => {
    if (onClick) {
      onClick(product);
    }
  };

  return (
    <Box
      className={clsx(classes.productItem, {
        [classes.productItemSelected]: isSelected,
      })}
      onClick={handleClick}
      data-product-id={product.id}
    >
      <Box className={classes.productItemInfo}>
        {/* Linha 1: Nome do medicamento */}
        <Typography className={classes.productName} title={product.name}>
          {product.name}
        </Typography>
        {/* Linha 2: Princípio ativo/fórmula */}
        {getValidActivePrinciple(product.activePrinciple) && (
          <Typography className={classes.productActivePrinciple} title={getValidActivePrinciple(product.activePrinciple)}>
            {getValidActivePrinciple(product.activePrinciple)}
          </Typography>
        )}
        {/* Linha 3: EAN e Estoque */}
        <Box className={classes.productDetails}>
          {product.barcode && (
            <span className={classes.productBarcode}>EAN: {product.barcode}</span>
          )}
          {showStock && (
            <span
              className={clsx(classes.productStock, {
                [classes.productStockLow]: isLowStock,
                [classes.productStockOut]: isOutOfStock,
              })}
            >
              {isOutOfStock ? "Sem estoque" : `${stockQuantity} un`}
            </span>
          )}
        </Box>
      </Box>

      {/* Price above, controls below */}
      <Box className={classes.productPriceSection}>
        <Typography className={classes.productPrice}>
          {formatCurrency(product.price || product.salePrice)}
        </Typography>

        {/* Quantity Controls + Cart Button */}
        <Box className={classes.productPriceControls}>
          <Box className={classes.productQuantityControls}>
          <Tooltip title="Diminuir">
            <span>
              <IconButton
                className={classes.productQtyButton}
                onClick={handleDecrease}
                disabled={quantity <= 1 || isOutOfStock}
                size="small"
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography className={classes.productQtyDisplay}>
            {quantity}
          </Typography>
          <Tooltip title="Aumentar">
            <span>
              <IconButton
                className={classes.productQtyButton}
                onClick={handleIncrease}
                disabled={isOutOfStock || (hasStockControl && quantity >= stockQuantity)}
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          </Box>

          {/* Send Product Button */}
          {onSend && (
            <Tooltip title="Enviar para o cliente">
              <span>
                <IconButton
                  className={classes.productSendButton}
                  onClick={handleSendProduct}
                  disabled={isSending}
                  size="small"
                  style={{ color: theme.palette.success.main }}
                >
                  {isSending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SendIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Add to Cart Button */}
          <Tooltip title={isOutOfStock ? "Sem estoque" : "Adicionar"}>
            <span>
              <IconButton
                className={classes.productAddButton}
                color="primary"
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                size="small"
              >
                <CartIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(PdvProductItem);
